import React, { memo, useState } from 'react'
import PropTypes from 'prop-types'
import { AlertTitle, Box, Button, Collapse, SvgIcon, Typography, useTheme } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { common, blueGrey } from '@mui/material/colors'
import { ReactComponent as FrameIcon } from 'assets/Frame.svg'
import { HHAlert } from 'components/common/HHAlert'
import T from 'T'

const isMac = typeof navigator !== 'undefined' && navigator.userAgent.toUpperCase().indexOf('MAC') > -1

const Kbd = ({ children }) => (
  <Box
    component="span"
    sx={{
      padding: '6px',
      display: 'inline-block',
      whiteSpace: 'nowrap',
      margin: '0 1px',
      lineHeight: '10px',
      verticalAlign: 'middle',
      borderRadius: '6px',
      backgroundColor: 'rgba(255,255,255,0.15)',
      border: '1px solid rgba(255,255,255,0.3)',
      fontFamily: 'monospace',
      fontSize: '0.85em',
      color: 'inherit',
    }}
  >
    {children}
  </Box>
)

Kbd.propTypes = { children: PropTypes.node.isRequired }

const CtrlKey = () => (isMac ? <Kbd>&#8984;</Kbd> : <Kbd>Ctrl</Kbd>)

const MapSelectionHelpButton = () => {
  const [isOpen, setIsOpen] = useState(true)
  const theme = useTheme()

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ mb: 0.5 }}>
          <HHAlert
            action={
              <Button variant="contained" onClick={() => setIsOpen(false)}>
                {T.DISMISS}
              </Button>
            }
            icon={
              <SvgIcon>
                <FrameIcon />
              </SvgIcon>
            }
            sx={{ bgcolor: blueGrey[700], color: common.white }}
            severity="info"
            borderColor={theme.palette.info.main}
          >
            <AlertTitle variant="h5">Pin Selection</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>
                <Typography variant="body2" color="inherit" component="span">
                  <strong>{'Select: '}</strong>
                  <Kbd>Shift</Kbd>
                  {' + drag'}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="inherit" component="span">
                  <strong>{'Deselect: '}</strong>
                  <CtrlKey />
                  {' + '}
                  <Kbd>Shift</Kbd>
                  {' + drag'}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="inherit" component="span">
                  <strong>{'Toggle: '}</strong>
                  Click on a pin
                </Typography>
              </li>
            </Box>
          </HHAlert>
        </Box>
      </Collapse>
      <Button
        onClick={() => setIsOpen(prev => !prev)}
        sx={{
          minWidth: 48,
          width: 48,
          height: 16,
          bgcolor: 'primary.main',
          color: common.white,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          p: 0,
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </Button>
    </Box>
  )
}

export default memo(MapSelectionHelpButton)
